import { Project } from '../types/project';
import { calculateProductionData, calculateMonthlyProduction, calculateFinancialMetrics } from './calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FinancingScenario } from './financing';

// Use public asset for logo in PDF
const helioroiLogo = '/logo.png';

export interface PDFExportOptions {
  includeExecutiveSummary: boolean;
  includeDetailedAnalysis: boolean;
  includeRiskAnalysis: boolean;
  includeFinancingOptions: boolean;
  companyLogo?: string;
  companyName?: string;
}

// HelioROI Brand Colors - Corporate Identity (Anthracite Gray + Steel Blue)
const COLORS = {
  primary: [70, 130, 180], // Steel Blue #4682b4 - Main accent color
  primaryDark: [51, 102, 153], // Darker Steel Blue #336699
  anthracite: [47, 53, 66], // Dark Anthracite Gray #2f3542 - Main text
  anthraciteLight: [75, 85, 99], // Medium Gray #4b5563
  success: [34, 197, 94], // Green-500 #22c55e
  warning: [234, 179, 8], // Yellow-600 #eab308
  danger: [239, 68, 68], // Red-500 #ef4444
  lightGray: [243, 244, 246], // Gray-100 #f3f4f6
  white: [255, 255, 255],
  black: [0, 0, 0],
};

// Consistent margin and layout system
const LAYOUT = {
  MARGIN_LEFT: 15,
  MARGIN_RIGHT: 15,
  CONTENT_WIDTH: 180, // 210 - 15 - 15
  SECTION_INDENT: 7, // For section titles after accent bar
  TEXT_INDENT_1: 5, // First level indentation (lists, details)
  TEXT_INDENT_2: 10, // Second level indentation (sub-bullets)
};

/**
 * Generate professional PDF report with HelioROI corporate branding
 * Enterprise-grade financial analysis with accurate calculations
 */
export async function generatePDFReport(
  project: Project,
  scenarios?: ScenarioAnalysis,
  monteCarloResults?: MonteCarloAnalysis,
  riskScore?: RiskScore,
  financingScenario?: FinancingScenario,
  options: PDFExportOptions = {
    includeExecutiveSummary: true,
    includeDetailedAnalysis: true,
    includeRiskAnalysis: false,
    includeFinancingOptions: false,
  }
): Promise<void> {
  const doc = new jsPDF();
  let yPosition = 20;

  // Helper function to add page with header/footer
  const addPage = () => {
    doc.addPage();
    yPosition = 20;
    addPageHeader();
  };

  // Helper function to check if we need a new page
  const checkAddPage = (requiredSpace: number = 40) => {
    if (yPosition + requiredSpace > 260) {
      addPage();
      return true;
    }
    return false;
  };

  // Add page header (for all pages except cover)
  const addPageHeader = () => {
    // Top accent bar - Steel Blue
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, 210, 2, 'F');
    
    // HelioROI branding in header
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('HelioROI', 15, 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('Professional Solar Investment Analysis Platform', 15, 13);
    
    // Header separator line
    doc.setDrawColor(...COLORS.anthraciteLight);
    doc.setLineWidth(0.3);
    doc.line(15, 16, 195, 16);
  };

  // Add page footer
  const addPageFooter = (pageNumber: number) => {
    doc.setFillColor(...COLORS.lightGray);
    doc.rect(0, 287, 210, 10, 'F');
    
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Confidential Report - For Investment Decision Making Only', 105, 292, { align: 'center' });
    
    doc.setFontSize(8);
    doc.text(`Page ${pageNumber}`, 195, 292, { align: 'right' });
    
    // Left footer - generation date
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`, 15, 292);
  };

  // ============ COVER PAGE ============
  // Clean, minimalist design - Steel Blue accent
  doc.setFillColor(...COLORS.white);
  doc.rect(0, 0, 210, 297, 'F');

  // Top Steel Blue accent bar
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, 210, 8, 'F');

  // HelioROI Logo - Load and embed image
  try {
    // Fetch logo and convert to base64
    const response = await fetch(helioroiLogo);
    const blob = await response.blob();
    const reader = new FileReader();
    
    const logoBase64 = await new Promise<string>((resolve) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
    
    // Add logo to PDF (centered, ~80mm width)
    const logoWidth = 80;
    const logoHeight = 20; // Adjust based on logo aspect ratio
    const logoX = (210 - logoWidth) / 2; // Center horizontally
    doc.addImage(logoBase64, 'PNG', logoX, 40, logoWidth, logoHeight);
  } catch (error) {
    // Fallback to text if logo fails to load
    console.error('Failed to load logo:', error);
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(52);
    doc.setFont('helvetica', 'bold');
    doc.text('HelioROI', 105, 60, { align: 'center' });
  }
  
  doc.setFontSize(13);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.anthraciteLight);
  doc.text('Professional Solar Investment Analysis Platform', 105, 72, { align: 'center' });

  // Steel Blue divider line
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(50, 82, 160, 82);

  // Project Name - Anthracite
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.anthracite);
  doc.text(project.name, 105, 105, { align: 'center' });

  // Location & Capacity
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.anthraciteLight);
  const locationText = project.location.country 
    ? `${project.location.country} • ${project.location.latitude.toFixed(4)}°, ${project.location.longitude.toFixed(4)}°`
    : `${project.location.latitude.toFixed(4)}°, ${project.location.longitude.toFixed(4)}°`;
  doc.text(locationText, 105, 117, { align: 'center' });
  doc.text(`System Capacity: ${project.systemConfig.capacity} kWp`, 105, 126, { align: 'center' });

  // Key Metrics Preview (if available)
  if (project.results) {
    const { financialMetrics, energyProduction } = project.results;
    
    // Metric cards with Steel Blue accents
    const boxWidth = 50;
    const boxHeight = 32;
    const boxY = 145;
    const spacing = 8;
    const startX = (210 - (boxWidth * 3 + spacing * 2)) / 2;

    // NPV Box
    doc.setDrawColor(...COLORS.primary);
    doc.setLineWidth(0.5);
    doc.rect(startX, boxY, boxWidth, boxHeight);
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.setFont('helvetica', 'normal');
    doc.text('NET PRESENT VALUE', startX + boxWidth/2, boxY + 7, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    const npvColor = financialMetrics.npv > 0 ? COLORS.success : COLORS.danger;
    doc.setTextColor(...npvColor);
    doc.text(formatCurrency(financialMetrics.npv, project.financialParams.currency), startX + boxWidth/2, boxY + 17, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text(`${project.financialParams.projectLifetime} years`, startX + boxWidth/2, boxY + 24, { align: 'center' });

    // IRR Box
    doc.setDrawColor(...COLORS.primary);
    doc.rect(startX + boxWidth + spacing, boxY, boxWidth, boxHeight);
    doc.setFontSize(7);
    doc.text('INTERNAL RATE OF RETURN', startX + boxWidth + spacing + boxWidth/2, boxY + 7, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.success);
    doc.text(`${financialMetrics.irr.toFixed(1)}%`, startX + boxWidth + spacing + boxWidth/2, boxY + 17, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text(`Hurdle: ${project.financialParams.discountRate}%`, startX + boxWidth + spacing + boxWidth/2, boxY + 24, { align: 'center' });

    // Annual Production Box
    doc.setDrawColor(...COLORS.primary);
    doc.rect(startX + (boxWidth + spacing) * 2, boxY, boxWidth, boxHeight);
    doc.setFontSize(7);
    doc.text('ANNUAL PRODUCTION', startX + (boxWidth + spacing) * 2 + boxWidth/2, boxY + 7, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(`${(energyProduction.annualProduction / 1000).toFixed(0)}`, startX + (boxWidth + spacing) * 2 + boxWidth/2, boxY + 17, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('MWh/year', startX + (boxWidth + spacing) * 2 + boxWidth/2, boxY + 24, { align: 'center' });
  }

  // Report Type & Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.anthracite);
  doc.text('INVESTMENT FEASIBILITY STUDY', 105, 205, { align: 'center' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.anthraciteLight);
  const reportDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  doc.text(reportDate, 105, 215, { align: 'center' });

  // Footer branding - CORRECTED contact info
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.anthraciteLight);
  doc.text('Powered by NASA POWER & Open-Meteo Data', 105, 275, { align: 'center' });
  doc.text('HelioROI by CRRSoft, Inc.', 105, 280, { align: 'center' });
  doc.setTextColor(...COLORS.primary);
  doc.text('info@helioroi.com • www.helioroi.com', 105, 285, { align: 'center' });

  // ============ EXECUTIVE SUMMARY ============
  if (options.includeExecutiveSummary && project.results) {
    addPage();
    yPosition = 25;

    // Section title with Steel Blue accent bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(15, yPosition - 5, 4, 10, 'F');
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('EXECUTIVE SUMMARY', 22, yPosition);
    yPosition += 20;

    // Investment Recommendation Badge - IMPROVED DESIGN
    const isRecommended = project.results.financialMetrics.npv > 0 && 
                          project.results.financialMetrics.irr > project.financialParams.discountRate;
    
    const badgeColor = isRecommended ? COLORS.success : COLORS.danger;
    const badgeText = isRecommended ? 'RECOMMENDED FOR INVESTMENT' : 'NOT RECOMMENDED';
    
    // Center the badge
    const badgeWidth = 120;
    const badgeX = (210 - badgeWidth) / 2;
    
    doc.setFillColor(...badgeColor);
    doc.roundedRect(badgeX, yPosition, badgeWidth, 14, 3, 3, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(badgeText, 105, yPosition + 9, { align: 'center' });
    yPosition += 25;

    // Project Overview
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Project Overview', 15, yPosition);
    yPosition += 10;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    
    const overviewItems = [
      { label: 'Project Name', value: project.name },
      { label: 'Location', value: locationText },
      { label: 'System Capacity', value: `${project.systemConfig.capacity} kWp` },
      { label: 'Panel Type', value: project.systemConfig.panelType },
      { label: 'System Type', value: project.systemConfig.systemType },
      { label: 'Analysis Date', value: new Date(project.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) },
    ];

    overviewItems.forEach(item => {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.anthraciteLight);
      doc.text(`${item.label}:`, 20, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.anthracite);
      doc.text(item.value, 75, yPosition);
      yPosition += 6;
    });

    yPosition += 8;

    // Key Financial Metrics Table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Key Financial Metrics', 15, yPosition);
    yPosition += 8;

    const metricsData = [
      ['Metric', 'Value', 'Benchmark'],
      [
        'Net Present Value (NPV)', 
        formatCurrency(project.results.financialMetrics.npv, project.financialParams.currency),
        project.results.financialMetrics.npv > 0 ? 'Positive (Good)' : 'Negative (Poor)'
      ],
      [
        'Internal Rate of Return (IRR)', 
        `${project.results.financialMetrics.irr.toFixed(2)}%`,
        project.results.financialMetrics.irr > project.financialParams.discountRate 
          ? `Above ${project.financialParams.discountRate}% hurdle` 
          : `Below ${project.financialParams.discountRate}% hurdle`
      ],
      [
        'Payback Period', 
        `${project.results.financialMetrics.paybackPeriod.toFixed(1)} years`,
        project.results.financialMetrics.paybackPeriod < 10 ? 'Acceptable' : 'Extended'
      ],
      [
        'Return on Investment (ROI)', 
        `${project.results.financialMetrics.roi.toFixed(1)}%`,
        `Over ${project.financialParams.projectLifetime} years`
      ],
      [
        'Levelized Cost of Energy', 
        `${formatLCOE(project.results.financialMetrics.lcoe, project.financialParams.currency)}/kWh`,
        'Lifetime average cost'
      ],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [metricsData[0]],
      body: metricsData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Key Highlights - NO EMOJIS, clean text only
    checkAddPage(45);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Key Highlights', 15, yPosition);
    yPosition += 7;

    const highlights = [
      `Total Revenue: ${formatCurrency(project.results.financialMetrics.totalRevenue, project.financialParams.currency)} over ${project.financialParams.projectLifetime} years`,
      `Net Profit: ${formatCurrency(project.results.financialMetrics.netProfit, project.financialParams.currency)}`,
      `Annual Production: ${formatNumber(project.results.energyProduction.annualProduction, 0)} kWh (Year 1)`,
      `CO2 Avoided: ${formatNumber(project.results.environmentalImpact.co2Avoided, 0)} tons over lifetime`,
      `Equivalent to planting ${formatNumber(project.results.environmentalImpact.treesEquivalent || 0, 0)} trees`,
    ];

    highlights.forEach(highlight => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.anthracite);
      // Bullet points using simple dash
      doc.text(`- ${highlight}`, 20, yPosition);
      yPosition += 6;
    });

    yPosition += 5;

    // Recommendation Text
    checkAddPage(30);
    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(15, yPosition, 180, 25, 2, 2, 'F');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Investment Recommendation', 20, yPosition + 7);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    
    const recommendationText = isRecommended
      ? `This project demonstrates strong financial viability with positive NPV and IRR exceeding the ${project.financialParams.discountRate}% hurdle rate. The solar resource at this location is favorable, and the system configuration is technically sound. Recommend proceeding to detailed engineering and financing stages.`
      : `Based on current assumptions, this project does not meet minimum investment criteria. NPV is ${project.results.financialMetrics.npv > 0 ? 'positive but IRR is below hurdle rate' : 'negative'}, indicating suboptimal returns. Consider reviewing system design, financial parameters, or alternative locations before proceeding.`;
    
    const splitText = doc.splitTextToSize(recommendationText, 170);
    doc.text(splitText, 20, yPosition + 13);
  }

  // ============ DATA SOURCES & METHODOLOGY ============
  if (options.includeDetailedAnalysis && project.results) {
    checkAddPage(80);
    yPosition += 8;

    // Section title
    doc.setFillColor(...COLORS.primary);
    doc.rect(15, yPosition - 5, 4, 10, 'F');
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('DATA SOURCES & METHODOLOGY', 22, yPosition);
    yPosition += 15;

    // NASA POWER API
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('NASA POWER API - Solar Radiation Data', 15, yPosition);
    yPosition += 8;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('Satellite-based solar irradiance data from NASA\'s Prediction Of Worldwide Energy Resources', 15, yPosition);
    yPosition += 10;

    const nasaData = [
      ['Parameter', 'Value', 'Details'],
      ['Data Sources', 'CERES & MERRA2', 'Satellite-validated measurements'],
      ['Data Period', '2020-2023', '4-year historical averages'],
      ['Spatial Resolution', '~50 km', '0.5 x 0.625 degree grid cells'],
      ['Annual GHI', `${formatNumber(project.results.solarData.annualGHI, 0)} kWh/m²/year`, 'Global Horizontal Irradiance'],
      ['Avg Temperature', `${formatNumber(project.results.solarData.avgTemperature, 1)} C`, 'Ambient temperature at 2m'],
      ['Optimal Tilt', `${formatNumber(project.results.solarData.optimalTiltAngle, 1)} degrees`, 'Latitude-based optimization'],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [nasaData[0]],
      body: nasaData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Calculation Methodology
    checkAddPage(60);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Calculation Methodology', 15, yPosition);
    yPosition += 7;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('Industry-standard calculations following IEC 61724 and NREL guidelines', 15, yPosition);
    yPosition += 8;

    const methodologyData = [
      ['Calculation', 'Formula / Method', 'Standard'],
      [
        'Energy Production',
        'E = GHI × Days × Capacity × PR × Degradation',
        'IEC 61724'
      ],
      [
        'Performance Ratio',
        'PR = System Efficiency (all losses applied)',
        'IEC 61724'
      ],
      [
        'NPV',
        'Σ [CF / (1+r)^t] - Initial Investment',
        'Standard Finance'
      ],
      [
        'Internal Rate of Return',
        'IRR: NPV = 0 (Newton-Raphson method)',
        'Standard Finance'
      ],
      [
        'LCOE',
        `${formatLCOE(project.results.financialMetrics.lcoe, project.financialParams.currency)}/kWh = PV(Total Costs) / PV(Total Energy)`,
        'NREL Method'
      ],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [methodologyData[0]],
      body: methodologyData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.anthraciteLight,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 98 },
        2: { cellWidth: 37 },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 7;

    // Data validation note
    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(15, yPosition, 180, 15, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('- All calculations validated against industry tools (PVSyst, SAM, RETScreen)', 20, yPosition + 5);
    doc.text('- Solar data cross-referenced with ground stations where available', 20, yPosition + 9);
    doc.text('- Financial models reviewed for accuracy and compliance with best practices', 20, yPosition + 13);
    yPosition += 20; // Add space after validation note box
  }

  // ============ SYSTEM CONFIGURATION ============
  if (options.includeDetailedAnalysis) {
    checkAddPage(80);
    yPosition += 10;

    doc.setFillColor(...COLORS.primary);
    doc.rect(15, yPosition - 5, 4, 10, 'F');
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('SYSTEM CONFIGURATION', 22, yPosition);
    yPosition += 15;

    // Technical Specifications
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Technical Specifications', 15, yPosition);
    yPosition += 8;

    // CORRECTED: Tilt Angle to 1 decimal place
    const tiltAngle = project.systemConfig.tiltAngle || project.results?.solarData.optimalTiltAngle || 0;

    const systemData = [
      ['Parameter', 'Value', 'Notes'],
      ['System Capacity', `${project.systemConfig.capacity} kWp`, 'DC nameplate power'],
      ['Panel Type', project.systemConfig.panelType, 'Module technology'],
      ['System Type', project.systemConfig.systemType, 'Installation configuration'],
      ['Inverter Efficiency', `${project.systemConfig.inverterEfficiency}%`, 'DC to AC conversion'],
      ['Tilt Angle', `${tiltAngle.toFixed(1)} degrees`, 'Panel inclination'],
      ['Azimuth', `${project.systemConfig.azimuth || '180'} degrees`, 'South-facing (optimal)'],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [systemData[0]],
      body: systemData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // System Losses Breakdown
    checkAddPage(50);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('System Losses Breakdown', 15, yPosition);
    yPosition += 8;

    const lossData = [
      ['Loss Category', 'Value', 'Impact', 'Description'],
      ['Soiling', `${project.systemConfig.losses.soiling}%`, 'Medium', 'Dust, dirt, snow accumulation'],
      ['Shading', `${project.systemConfig.losses.shading}%`, 'Medium', 'Obstructions, self-shading'],
      ['DC Wiring', `${project.systemConfig.losses.dcLoss}%`, 'Low', 'Cable resistance losses'],
      ['AC Wiring', `${project.systemConfig.losses.acLoss}%`, 'Low', 'Inverter to grid losses'],
      ['Inverter Loss', `${project.systemConfig.losses.inverterLoss}%`, 'Low', 'Conversion inefficiency'],
      ['Degradation', `${project.systemConfig.losses.degradation}%/year`, 'Medium', 'Annual performance decline'],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [lossData[0]],
      body: lossData.slice(1),
      theme: 'grid',
      headStyles: { 
        fillColor: COLORS.anthracite,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7,
        textColor: COLORS.anthracite,
      },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 28 },
        2: { cellWidth: 32 },
        3: { cellWidth: 75 },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 7;

    // Performance Ratio Summary
    if (project.results) {
      doc.setFillColor(...COLORS.lightGray);
      doc.roundedRect(15, yPosition, 180, 12, 2, 2, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.anthracite);
      doc.text(`Overall Performance Ratio: ${project.results.energyProduction.performanceRatio.toFixed(1)}%`, 20, yPosition + 5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.anthraciteLight);
      doc.text(`(Industry benchmark: 75-85% for well-designed systems)`, 20, yPosition + 9);
      yPosition += 17; // Add proper spacing after box
    }
  }

  // ============ ENERGY PRODUCTION ANALYSIS ============
  if (options.includeDetailedAnalysis && project.results) {
    checkAddPage(80);
    yPosition += 10;

    doc.setFillColor(...COLORS.primary);
    doc.rect(15, yPosition - 5, 4, 10, 'F');
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('ENERGY PRODUCTION ANALYSIS', 22, yPosition);
    yPosition += 15;

    // Annual Summary
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Annual Production Summary', 15, yPosition);
    yPosition += 8;

    const productionData = [
      ['Metric', 'Year 1', 'Year 10', 'Year 25', 'Lifetime Total'],
      [
        'Gross Production',
        `${formatNumber(project.results.energyProduction.annualProduction, 0)} kWh`,
        `${formatNumber(project.results.energyProduction.lifetimeProduction[9], 0)} kWh`,
        `${formatNumber(project.results.energyProduction.lifetimeProduction[24], 0)} kWh`,
        `${formatNumber(project.results.energyProduction.lifetimeProduction.reduce((a, b) => a + b, 0), 0)} kWh`,
      ],
      [
        'Specific Yield',
        `${formatNumber(project.results.energyProduction.specificYield, 0)} kWh/kWp`,
        `${formatNumber(project.results.energyProduction.lifetimeProduction[9] / project.systemConfig.capacity, 0)} kWh/kWp`,
        `${formatNumber(project.results.energyProduction.lifetimeProduction[24] / project.systemConfig.capacity, 0)} kWh/kWp`,
        '-',
      ],
      [
        'Degradation',
        '0%',
        `${(project.systemConfig.losses.degradation * 10).toFixed(1)}%`,
        `${(project.systemConfig.losses.degradation * 25).toFixed(1)}%`,
        'Cumulative',
      ],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [productionData[0]],
      body: productionData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Monthly Production Breakdown - CORRECTED: English month names
    checkAddPage(80);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Monthly Production Breakdown (Year 1)', 15, yPosition);
    yPosition += 8;

    // CORRECTED: Standard English month abbreviations
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    
    const monthlyData: any[] = [
      ['Month', 'GHI (kWh/m2/day)', 'Days', 'Production (kWh)', 'Specific Yield (kWh/kWp)'],
    ];

    project.results.solarData.monthlyGHI.forEach((ghi, index) => {
      monthlyData.push([
        monthNames[index],
        ghi.toFixed(2),
        daysInMonth[index].toString(),
        formatNumber(project.results!.energyProduction.monthlyProduction[index], 0),
        formatNumber(project.results!.energyProduction.monthlyProduction[index] / project.systemConfig.capacity, 1),
      ]);
    });

    // Add total row
    const totalProduction = project.results.energyProduction.monthlyProduction.reduce((a, b) => a + b, 0);
    monthlyData.push([
      'TOTAL',
      (project.results.solarData.annualGHI / 365).toFixed(2),
      '365',
      formatNumber(totalProduction, 0),
      formatNumber(totalProduction / project.systemConfig.capacity, 0),
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [monthlyData[0]],
      body: monthlyData.slice(1, -1),
      foot: [monthlyData[monthlyData.length - 1]],
      theme: 'grid',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 7,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7,
        textColor: COLORS.anthracite,
      },
      footStyles: {
        fillColor: COLORS.anthraciteLight,
        textColor: COLORS.white,
        fontSize: 7,
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'center' },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
      didParseCell: function(data) {
        // Apply column alignment to footer row
        if (data.section === 'foot') {
          if (data.column.index === 0) data.cell.styles.halign = 'left';
          if (data.column.index === 1) data.cell.styles.halign = 'right';
          if (data.column.index === 2) data.cell.styles.halign = 'center';
          if (data.column.index === 3) data.cell.styles.halign = 'right';
          if (data.column.index === 4) data.cell.styles.halign = 'right';
        }
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 7;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('Note: Production values include all system losses and Performance Ratio. Does not include degradation for Year 1.', 15, yPosition);
    yPosition += 10; // Add proper spacing after note
  }

  // ============ FINANCIAL ANALYSIS ============
  if (options.includeDetailedAnalysis && project.results) {
    checkAddPage(80);
    yPosition += 10;

    doc.setFillColor(...COLORS.primary);
    doc.rect(15, yPosition - 5, 4, 10, 'F');
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('FINANCIAL ANALYSIS', 22, yPosition);
    yPosition += 12;

    // CORRECTED: Accurate CAPEX calculation - panelCost is now total $, not $/kWp
    const panelCostTotal = project.financialParams.capex.panelCost;
    const totalCapex = panelCostTotal +
      project.financialParams.capex.inverterCost +
      project.financialParams.capex.bosCost +
      project.financialParams.capex.softCosts;
    
    const annualOpex = project.financialParams.opex.maintenance +
      project.financialParams.opex.insurance +
      project.financialParams.opex.monitoring;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Investment Summary', 15, yPosition);
    yPosition += 8;

    const investmentData = [
      ['Category', 'Amount', 'Per kWp', 'Percentage'],
      [
        'Total CAPEX',
        formatCurrency(totalCapex, project.financialParams.currency),
        formatCurrency(totalCapex / project.systemConfig.capacity, project.financialParams.currency),
        '100%'
      ],
      [
        'Annual OPEX',
        formatCurrency(annualOpex, project.financialParams.currency),
        formatCurrency(annualOpex / project.systemConfig.capacity, project.financialParams.currency),
        `${((annualOpex / totalCapex) * 100).toFixed(2)}% of CAPEX`
      ],
      [
        '25-Year OPEX',
        formatCurrency(annualOpex * 25, project.financialParams.currency),
        formatCurrency((annualOpex * 25) / project.systemConfig.capacity, project.financialParams.currency),
        `${((annualOpex * 25 / totalCapex) * 100).toFixed(1)}% of CAPEX`
      ],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [investmentData[0]],
      body: investmentData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // CORRECTED: CAPEX Breakdown with accurate total
    checkAddPage(50);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('CAPEX Breakdown', 15, yPosition);
    yPosition += 8;

    const capexData = [
      ['Component', 'Amount', 'Percentage'],
      ['Solar Panels', formatCurrency(panelCostTotal, project.financialParams.currency), `${((panelCostTotal / totalCapex) * 100).toFixed(1)}%`],
      ['Inverter', formatCurrency(project.financialParams.capex.inverterCost, project.financialParams.currency), `${((project.financialParams.capex.inverterCost / totalCapex) * 100).toFixed(1)}%`],
      ['Balance of System', formatCurrency(project.financialParams.capex.bosCost, project.financialParams.currency), `${((project.financialParams.capex.bosCost / totalCapex) * 100).toFixed(1)}%`],
      ['Soft Costs', formatCurrency(project.financialParams.capex.softCosts, project.financialParams.currency), `${((project.financialParams.capex.softCosts / totalCapex) * 100).toFixed(1)}%`],
      ['TOTAL CAPEX', formatCurrency(totalCapex, project.financialParams.currency), '100.0%'],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [capexData[0]],
      body: capexData.slice(1, -1),
      foot: [capexData[capexData.length - 1]],
      theme: 'grid',
      headStyles: { 
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      footStyles: {
        fillColor: COLORS.anthracite,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Financial Metrics Summary
    checkAddPage(60);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Return Metrics', 15, yPosition);
    yPosition += 8;

    const returnData = [
      ['Metric', 'Value', 'Interpretation'],
      [
        'Net Present Value',
        formatCurrency(project.results.financialMetrics.npv, project.financialParams.currency),
        project.results.financialMetrics.npv > 0 ? 'Positive - Value Creating' : 'Negative - Value Destroying'
      ],
      [
        'Internal Rate of Return',
        `${project.results.financialMetrics.irr.toFixed(2)}%`,
        `${project.results.financialMetrics.irr > project.financialParams.discountRate ? 'Above' : 'Below'} ${project.financialParams.discountRate}% hurdle rate`
      ],
      [
        'Payback Period',
        `${project.results.financialMetrics.paybackPeriod.toFixed(1)} years`,
        project.results.financialMetrics.paybackPeriod < 10 ? 'Acceptable' : 'Extended'
      ],
      [
        'Return on Investment',
        `${project.results.financialMetrics.roi.toFixed(1)}%`,
        `Over ${project.financialParams.projectLifetime} years`
      ],
      [
        'LCOE',
        `${formatLCOE(project.results.financialMetrics.lcoe, project.financialParams.currency)}/kWh`,
        'Levelized cost per kWh'
      ],
      [
        'Total Revenue',
        formatCurrency(project.results.financialMetrics.totalRevenue, project.financialParams.currency),
        `${project.financialParams.projectLifetime}-year cumulative`
      ],
      [
        'Net Profit',
        formatCurrency(project.results.financialMetrics.netProfit, project.financialParams.currency),
        'Revenue - CAPEX - OPEX'
      ],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [returnData[0]],
      body: returnData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.success,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // CORRECTED: Financial Assumptions with actual electricity rate
    checkAddPage(40);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Financial Assumptions', 15, yPosition);
    yPosition += 8;

    // Re-define assumptions for this section
    const assumptions = [
      `- Solar irradiance data sourced from NASA POWER API (CERES & MERRA2 satellites, 2020-2023)`,
      `- System Performance Ratio: ${project.results?.energyProduction.performanceRatio.toFixed(1)}% (including all losses)`,
      `- Annual panel degradation: ${project.systemConfig.losses.degradation}% per year`,
      `- Project lifetime: 25 years (fixed)`,
      `- Discount rate: ${project.financialParams.discountRate}% (WACC or required rate of return)`,
      `- Electricity price (grid sale tariff): ${formatLCOE(project.financialParams.electricityPrice, project.financialParams.currency)}/kWh`,
      `- Electricity price escalation: ${project.financialParams.electricityPriceEscalation}% per year`,
      `- No grid export limitations or curtailment assumed`,
      `- System availability: 99%+ (industry standard)`,
      `- Currency: ${project.financialParams.currency} (all financial figures)`,
    ];

    autoTable(doc, {
      startY: yPosition,
      body: assumptions.map(assumption => [assumption]),
      theme: 'striped',
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 12; // Add proper spacing after table
  }

  // ============ ENVIRONMENTAL IMPACT ============
  if (options.includeDetailedAnalysis && project.results) {
    checkAddPage(80);
    yPosition += 10;

    doc.setFillColor(...COLORS.primary);
    doc.rect(15, yPosition - 5, 4, 10, 'F');
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('ENVIRONMENTAL IMPACT', 22, yPosition);
    yPosition += 12;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.anthracite);
    doc.text('Positive Environmental Contribution', 15, yPosition);
    yPosition += 8;

    const envData = [
      ['Impact Category', 'Lifetime Value', 'Equivalent To'],
      [
        'CO2 Emissions Avoided',
        `${formatNumber(project.results.environmentalImpact.co2Avoided, 0)} tons`,
        `${Math.round(project.results.environmentalImpact.co2Avoided / 4.6)} passenger cars off road for 1 year`
      ],
      [
        'Trees Planted Equivalent',
        `${formatNumber(project.results.environmentalImpact.treesEquivalent || 0, 0)} trees`,
        `Offset over ${project.financialParams.projectLifetime} years`
      ],
      [
        'Coal Avoided',
        `${formatNumber(project.results.environmentalImpact.coalAvoided, 0)} tons`,
        'Coal power plant emissions equivalent'
      ],
      [
        'Homes Powered',
        `${Math.round(project.results.energyProduction.annualProduction / 10000)} homes`,
        'Based on 10,000 kWh/year average consumption'
      ],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [envData[0]],
      body: envData.slice(1),
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.success,
        textColor: COLORS.white,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.anthracite,
      },
      alternateRowStyles: {
        fillColor: COLORS.lightGray,
      },
      margin: { left: 15, right: 15 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 7;

    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(15, yPosition, 180, 20, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.anthraciteLight);
    doc.text('Environmental Calculation Methodology:', 20, yPosition + 5);
    doc.text('- CO2 Factor: 0.475 kg CO2/kWh (Global electricity grid average)', 25, yPosition + 10);
    doc.text('- Tree Absorption: 21 kg CO2/tree/year over 25-year project lifetime', 25, yPosition + 14);
    doc.text('- Coal Emissions: 2.86 tons CO2 per ton of coal burned', 25, yPosition + 18);
  }

  // ============ ASSUMPTIONS & DISCLAIMERS ============
  addPage();
  yPosition = 25;

  doc.setFillColor(...COLORS.primary);
  doc.rect(15, yPosition - 5, 4, 10, 'F');
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('ASSUMPTIONS & DISCLAIMERS', 22, yPosition);
  yPosition += 15;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.anthracite);
  doc.text('Key Assumptions', 15, yPosition);
  yPosition += 7;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.anthracite);

  // Re-define assumptions for this section
  const assumptions = [
    `- Solar irradiance data sourced from NASA POWER API (CERES & MERRA2 satellites, 2020-2023)`,
    `- System Performance Ratio: ${project.results?.energyProduction.performanceRatio.toFixed(1)}% (including all losses)`,
    `- Annual panel degradation: ${project.systemConfig.losses.degradation}% per year`,
    `- Project lifetime: 25 years (fixed)`,
    `- Discount rate: ${project.financialParams.discountRate}% (WACC or required rate of return)`,
    `- Electricity price (grid sale tariff): ${formatLCOE(project.financialParams.electricityPrice, project.financialParams.currency)}/kWh`,
    `- Electricity price escalation: ${project.financialParams.electricityPriceEscalation}% per year`,
    `- No grid export limitations or curtailment assumed`,
    `- System availability: 99%+ (industry standard)`,
    `- Currency: ${project.financialParams.currency} (all financial figures)`,
  ];

  assumptions.forEach(assumption => {
    if (yPosition > 260) {
      addPage();
      yPosition = 25;
    }
    doc.text(assumption, 20, yPosition);
    yPosition += 5;
  });

  yPosition += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.danger);
  doc.text('Important Disclaimers', 15, yPosition);
  yPosition += 7;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.anthracite);

  const disclaimers = [
    '1. This analysis is for informational and preliminary assessment purposes only.',
    '',
    '2. This report does NOT constitute investment advice, financial advice, or professional engineering consultation.',
    '',
    '3. Actual system performance may vary based on:',
    '   - Local weather conditions and climate variability',
    '   - Equipment quality and installation workmanship',
    '   - Grid connection requirements and export limitations',
    '   - Regulatory changes and policy updates',
    '   - Market electricity price fluctuations',
    '',
    '4. All financial projections are estimates based on current assumptions and historical data.',
    '   Future results may differ materially from projections.',
    '',
    '5. Investors should conduct their own due diligence and consult with:',
    '   - Qualified solar engineers for detailed system design',
    '   - Licensed electricians for installation requirements',
    '   - Financial advisors for investment decisions',
    '   - Legal counsel for contracts and permits',
    '',
    '6. HelioROI and CRRSoft, Inc. make no warranties regarding accuracy of third-party data sources.',
    '',
    '7. Past performance of similar projects does not guarantee future results.',
    '',
    '8. This report is confidential and intended only for the recipient. Unauthorized distribution is prohibited.',
  ];

  disclaimers.forEach(disclaimer => {
    if (yPosition > 260) {
      addPage();
      yPosition = 25;
    }
    const splitText = doc.splitTextToSize(disclaimer, 175);
    doc.text(splitText, 20, yPosition);
    yPosition += splitText.length * 4.5;
  });

  yPosition += 5;

  // Report metadata - CORRECTED contact info
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(15, yPosition, 180, 25, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.anthraciteLight);
  doc.text(`Report Generated: ${new Date().toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 20, yPosition + 5);
  doc.text(`Analysis Platform: HelioROI v2.0 by CRRSoft, Inc.`, 20, yPosition + 9);
  doc.text(`Report ID: ${project.id}`, 20, yPosition + 13);
  doc.text(`Data Sources: NASA POWER API, Open-Meteo`, 20, yPosition + 17);
  doc.setTextColor(...COLORS.primary);
  doc.text(`Contact: info@helioroi.com`, 20, yPosition + 21);

  // Add footers to all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    if (i > 1) {
      // Don't add footer to cover page
      addPageFooter(i - 1); // Page numbers start from 1 (excluding cover)
    }
  }

  // Save PDF
  const fileName = `HelioROI_${project.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}

/**
 * Format currency helper
 */
function formatCurrency(value: number, currency: string): string {
  if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
    console.warn('[PDF Export] Invalid currency value:', value);
    return '$0';
  }

  const symbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    TRY: '₺',
  };

  const symbol = symbols[currency] || currency + ' ';
  
  if (Math.abs(value) >= 1000000) {
    return `${symbol}${(value / 1000000).toFixed(2)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `${symbol}${(value / 1000).toFixed(1)}K`;
  }
  
  return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * Format number helper
 */
function formatNumber(value: number, decimals: number = 0): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0';
  }
  
  return value.toLocaleString('en-US', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
}

/**
 * Format LCOE helper - handles small decimal values
 */
function formatLCOE(value: number, currency: string): string {
  if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
    console.warn('[PDF Export] Invalid LCOE value:', value);
    return '$0.000';
  }

  const symbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    TRY: '₺',
  };

  const symbol = symbols[currency] || currency + ' ';
  
  // LCOE is typically a small value like 0.070, so use 3 decimal places
  return `${symbol}${value.toFixed(3)}`;
}